import axios from "axios";

/**
 * Базовый URL backend.
 *  - В dev / на разных доменах: задайте REACT_APP_BACKEND_URL=https://api.example.com в .env
 *  - В prod на том же домене: оставьте REACT_APP_BACKEND_URL пустым → пойдут относительные запросы /api/*
 *
 * Защита от 4 типичных проблем CRA build-time:
 *  1) undefined          → переменная не задана при сборке
 *  2) "undefined" строка → буквально такое попало в окружение
 *  3) trailing slash     → нормализуем
 *  4) "/api" уже в URL   → не дублируем (если разработчик случайно прописал её)
 */
const rawEnvUrl = process.env.REACT_APP_BACKEND_URL;
let _backend = (typeof rawEnvUrl === "string" && rawEnvUrl && rawEnvUrl !== "undefined")
  ? rawEnvUrl.replace(/\/+$/, "")
  : "";
// Если разработчик случайно прописал хвост /api в env-переменной — убираем его, чтобы не было /api/api/cars
if (_backend.endsWith("/api")) _backend = _backend.slice(0, -4);

export const BACKEND_URL = _backend;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "vezem_admin_token";
const REFRESH_KEY = "vezem_admin_refresh";
const ACTIVITY_KEY = "vezem_admin_last_activity";
const IDLE_LIMIT_MS = 30 * 60 * 1000; // 30 минут бездействия

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const getRefresh = () => localStorage.getItem(REFRESH_KEY);
export const setTokens = ({ access_token, refresh_token }) => {
  if (access_token) localStorage.setItem(TOKEN_KEY, access_token);
  if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
  localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
};
// backward compat
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(ACTIVITY_KEY);
};
export const touchActivity = () => localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
export const isIdleExpired = () => {
  const last = parseInt(localStorage.getItem(ACTIVITY_KEY) || "0", 10);
  return last > 0 && Date.now() - last > IDLE_LIMIT_MS;
};

export const api = axios.create({ baseURL: BACKEND_URL });

// Bulletproof: гарантируем, что в путь всегда попадёт /api/*.
// Защищает от ситуаций когда вызовы написаны как api.get("/cars") (без /api),
// а также от попыток вызвать /api/api/... повторно.
api.interceptors.request.use((config) => {
  // Принудительно проставляем baseURL, на случай если конфиг перезаписали
  if (config.baseURL == null) config.baseURL = BACKEND_URL;

  if (typeof config.url === "string") {
    // Абсолютные URL (с http/https) не трогаем
    const isAbsolute = /^https?:\/\//i.test(config.url);
    if (!isAbsolute) {
      let u = config.url;
      if (!u.startsWith("/")) u = "/" + u;
      // Если путь не начинается с /api/, дописываем префикс
      if (!u.startsWith("/api/") && u !== "/api") {
        u = "/api" + u;
      }
      config.url = u;
    }
  }

  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise = null;

api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";
    // refresh only on 401 from authed admin endpoints, and not on auth/refresh itself
    if (status === 401 && !original._retry && !url.includes("/auth/refresh") && !url.includes("/auth/login") && getRefresh()) {
      if (isIdleExpired()) {
        clearToken();
        if (window.location.pathname.startsWith("/administrator-panel")) window.location.reload();
        return Promise.reject(error);
      }
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = axios.post(`${API}/auth/refresh`, { refresh_token: getRefresh() })
            .then(r => { setTokens(r.data); return r.data.access_token; })
            .finally(() => { refreshPromise = null; });
        }
        const newAccess = await refreshPromise;
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newAccess}`;
        return api.request(original);
      } catch (e) {
        clearToken();
        if (window.location.pathname.startsWith("/administrator-panel")) window.location.reload();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  },
);

// Resolve image URL (supports backend uploads /api/uploads/... and external)
export const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  // /api/* → если задан абсолютный BACKEND_URL — префиксуем его, иначе оставляем относительный (тот же домен)
  if (url.startsWith("/api/")) return BACKEND_URL ? `${BACKEND_URL}${url}` : url;
  return url;
};

export const formatPrice = (n) => {
  if (n === null || n === undefined || n === "") return "";
  const num = Number(n);
  if (Number.isNaN(num)) return "";
  return new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(num) + " ₽";
};

export const formatMileage = (n) => {
  if (n === null || n === undefined) return "";
  return new Intl.NumberFormat("ru-RU").format(n) + " км";
};

export const formatApiError = (detail) => {
  if (detail == null) return "Что-то пошло не так";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const COUNTRY_FLAGS = {
  "Германия": "🇩🇪",
  "Франция": "🇫🇷",
  "Чехия": "🇨🇿",
  "Италия": "🇮🇹",
  "Нидерланды": "🇳🇱",
  "Польша": "🇵🇱",
  "Австрия": "🇦🇹",
  "Швеция": "🇸🇪",
  "Япония": "🇯🇵",
};

export const BODY_LABELS = {
  sedan: "Седан", suv: "Внедорожник", hatchback: "Хэтчбек",
  wagon: "Универсал", coupe: "Купе", minivan: "Минивэн",
};
export const FUEL_LABELS = {
  petrol: "Бензин", diesel: "Дизель", hybrid: "Гибрид", electric: "Электро",
};
export const TRANSMISSION_LABELS = {
  automatic: "Автомат", manual: "Механика", robot: "Робот", variator: "Вариатор",
};
